import React, { useRef, useState } from "react";
import { Dropdown, FormControl } from "react-bootstrap";
import { CountryPhoneWrapper } from "./CountryPhoneDropdown";
import { CountryWithStateModel } from "../models";
import {
  CountryFlag,
  DropdownWrapper,
  OptionsDropdownContainer,
  StyledDropdownButton,
} from "../styles";
import { CustomLabel, FlexContainer } from "@mindmemobile/mm-shared-global";

interface CountryDropdownProps {
  selectedCountry: CountryWithStateModel;
  error: boolean;
  errorMessage: string;
  onCountryChange: (countryId: string) => void;
  className?: string;
  availableCountries: CountryWithStateModel[];
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({
  selectedCountry,
  error,
  errorMessage,
  onCountryChange,
  className = "",
  availableCountries,
}) => {
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const filteredCountries = availableCountries.filter((country) =>
    country.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  // Focus the search box each time the menu opens (autoFocus only fires on the
  // initial mount, when the menu is still hidden). Also clear any stale search
  // text so the full list is shown on open.
  const handleToggle = (nextShow: boolean) => {
    if (nextShow) {
      setSearchText("");
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  const selectedCountryIndex = availableCountries.findIndex(
    (country: CountryWithStateModel) => country.id === selectedCountry.id,
  );

  const flagLink =
    selectedCountryIndex >= 0
      ? availableCountries[selectedCountryIndex]?.flagURL
      : "";

  const handleSelect = (eventKey: string | null) => {
    if (eventKey) {
      onCountryChange(eventKey);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <CountryPhoneWrapper className="country-phone-wrapper">
      <OptionsDropdownContainer
        className={`country-dpdn ps-country-dpdn ${className}`}
      >
        <DropdownWrapper error={error.toString()}>
          <StyledDropdownButton
            id="dropdown-basic-button"
            title={
              selectedCountry.id ? (
                <FlexContainer
                  alignItems="center"
                  className="styled-dropdown-flex"
                >
                  <CountryFlag
                    className="country-flag"
                    src={flagLink}
                    alt={selectedCountry.name}
                  />
                  <CustomLabel
                    className="custom-label"
                    fontSize="large"
                    fontWeight="light"
                  >
                    {selectedCountry.name}
                  </CustomLabel>
                </FlexContainer>
              ) : (
                <CustomLabel
                  fontSize="large"
                  fontWeight="light"
                  className="phone-data"
                >
                  Select Country
                </CustomLabel>
              )
            }
            onSelect={(eventKey: string | null) => handleSelect(eventKey)}
            onToggle={handleToggle}
            variant="light"
            className="zoomed-arrow"
          >
            <FormControl
              ref={searchInputRef}
              placeholder="Search Country..."
              onChange={handleSearch}
              value={searchText}
              className="dropdown-search-input"
              style={{
                outline: "none",
                boxShadow: "none",
                borderColor: "transparent",
              }}
            />
            {filteredCountries.map(
              (country: CountryWithStateModel, idx: number) => {
                return (
                  <>
                    <Dropdown.Item key={country.id} eventKey={country.id}>
                      <CountryFlag src={country.flagURL} alt={country.name} />
                      <span>{country.name}</span>
                    </Dropdown.Item>
                  </>
                );
              },
            )}
          </StyledDropdownButton>
        </DropdownWrapper>
        {error && <p className="error-message">{errorMessage}</p>}
      </OptionsDropdownContainer>
    </CountryPhoneWrapper>
  );
};

export default CountryDropdown;
